import { Suspense, lazy } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import type { Role } from '@sbg/shared/constants';
import { safeRedirectPath } from '@sbg/shared/utils';
import { AppLink } from '@/components/ui/AppLink';
import { buttonClasses } from '@/components/ui/button-classes';
import { Section } from '@/components/ui/Section';
import { Spinner } from '@/components/ui/Spinner';
import { hasRoleAtLeast, useAuth } from '@/lib/auth';
import { loginRedirectTarget } from '@/lib/auth/redirect';
import { useT } from '@/lib/i18n';
import { Seo } from '@/lib/seo';

/**
 * Outside the public shell (the back-office frame has no `<main>` of its own) a refusal or a
 * spinner must still sit in the page's one `<main>` landmark, which the skip link and the route
 * announcer target.
 */
function MainLandmark({ on, children }: { on: boolean; children: ReactNode }) {
  if (!on) return children;
  return (
    <main id="main" tabIndex={-1} className="app-main">
      {children}
    </main>
  );
}

/**
 * Shown while `GET /auth/me` decides who the visitor is (first load of a protected URL) and while an
 * anonymous visitor is being sent to the sign-in page. In the public shell it is as tall as the
 * space under the header (`min-h-…`), so the footer stays below the fold until the real page
 * replaces it: a short placeholder would show the footer high on the screen and then push it down
 * (a layout shift of ~0.8 on a phone). The back-office frame has no footer, so half a screen does.
 */
export function AuthPending({ landmark = false }: { landmark?: boolean }) {
  const t = useT();
  return (
    <MainLandmark on={landmark}>
      <div
        className={`grid place-items-center ${
          landmark ? 'min-h-[50dvh]' : 'min-h-[calc(100dvh_-_var(--header-h))]'
        }`}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3 text-fg-muted">
          <Spinner size={22} />
          <span className="t-small">{t('auth.checking')}</span>
        </div>
      </div>
    </MainLandmark>
  );
}

/** Signed in, but not allowed here (wrong role). Never redirects to login — that would loop. */
export function Forbidden({ landmark = false }: { landmark?: boolean }) {
  const t = useT();
  return (
    <MainLandmark on={landmark}>
      <Seo title={t('errors.forbidden.title')} noindex path="/" />
      <Section headingLevel={1} title={t('errors.forbidden.title')} divider={false}>
        <p className="max-w-[42rem] t-lead text-fg-muted">{t('errors.forbidden.body')}</p>
        <p className="mt-8">
          <AppLink to="/" className={buttonClasses({ variant: 'outline' })}>
            {t('common.backHome')}
          </AppLink>
        </p>
      </Section>
    </MainLandmark>
  );
}

/**
 * Layout route for signed-in areas. Anonymous → `/login?next=…`; while the session is being checked a
 * spinner shows (no flash of the login page for signed-in users). This is UX only — the API enforces
 * authentication and authorisation on every request.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') return <AuthPending />;
  if (status === 'anonymous') {
    // The sign-in page is a lazy chunk: until it has loaded the router keeps this element on
    // screen, so it keeps the placeholder's height (an empty <main> would pull the footer up).
    return (
      <>
        <Navigate
          to={loginRedirectTarget(location.pathname, location.search, location.hash)}
          replace
        />
        <AuthPending />
      </>
    );
  }
  return children ?? <Outlet />;
}

/**
 * Layout route requiring at least `minRole` (`ADMIN` covers ADMIN + SUPER_ADMIN; `SUPER_ADMIN` only
 * Super Admins). Anonymous → login; signed in with too little privilege → a 403 page.
 * UX only: the API authorises every request again, and the bundle contains no secrets.
 * `landmark`: the guard sits outside the public shell, so its spinner/refusal bring their own `<main>`.
 */
export function RequireRole({
  minRole,
  landmark = false,
  children,
}: {
  minRole: Role;
  landmark?: boolean;
  children?: ReactNode;
}) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') return <AuthPending landmark={landmark} />;
  if (status === 'anonymous' || !user) {
    // The back-office IS the sign-in address (D-391): `/admin` asks for the Google login right where the visitor is, and
    // signing in brings them back to the very page they asked for. Anywhere else a guard sends the visitor there (to `/admin`,
    // never back to itself: that is why the check is on the address, not on who asked).
    if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
      return <AdminSignIn landmark={landmark} />;
    }
    return (
      <>
        <Navigate
          to={loginRedirectTarget(location.pathname, location.search, location.hash)}
          replace
        />
        <AuthPending landmark={landmark} />
      </>
    );
  }
  const backFromGoogle = googleReturnTarget(location.search);
  if (landmark && backFromGoogle !== null) return <Navigate to={backFromGoogle} replace />;
  if (!hasRoleAtLeast(user.role, minRole)) return <Forbidden landmark={landmark} />;
  return children ?? <Outlet />;
}

// The sign-in page is a chunk of its own: a signed-in member of staff never downloads it.
const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((module) => ({ default: module.LoginPage })),
);

/**
 * The Google login shown by the back-office frame to a signed-out visitor, at the address they asked for. `next` is the page
 * they wanted (nothing at `/admin` itself: the back-office home is where staff land anyway).
 */
function AdminSignIn({ landmark }: { landmark: boolean }) {
  const { pathname, search } = useLocation();
  const next = pathname.replace(/\/+$/, '') === '/admin' ? '' : `${pathname}${search}`;
  return (
    <MainLandmark on={landmark}>
      <Suspense fallback={<AuthPending />}>
        <LoginPage next={next} />
      </Suspense>
    </MainLandmark>
  );
}

/**
 * Coming back from Google the address is `/admin?via=google[&next=…]` (the round trip passes through the old `/login`
 * address): once the session is read, staff are taken to the page they wanted and the marker leaves the address bar.
 * `null` when this is no such return.
 */
function googleReturnTarget(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.get('via') !== 'google') return null;
  const target = safeRedirectPath(params.get('next'), '/admin');
  return target === '/login' || target.startsWith('/login?') || target.startsWith('/login/')
    ? '/admin'
    : target;
}
